import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import dayjs from 'dayjs';

import { useFetchTrazabilidad } from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-clientes.actions';
import { ITrazabilidadCliente } from '@/shared/interfaces/erp/tickets-soporte-clientes';

type TabTrazabilidadProps = {
  clienteId: number;
  contratoId: number;
};

type DotColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'grey';

const getColorByTipoCambio = (tipo: string): DotColor => {
  const lower = tipo.toLowerCase();
  if (lower.includes('plan') || lower.includes('servicio')) return 'info';
  if (lower.includes('estado')) return 'warning';
  if (lower.includes('tecnico') || lower.includes('equipo')) return 'secondary';
  if (lower.includes('financiero') || lower.includes('pago')) return 'success';
  if (lower.includes('cancelacion') || lower.includes('suspension')) return 'error';
  return 'primary';
};

export default function TabTrazabilidad({ clienteId, contratoId }: TabTrazabilidadProps) {
  const { data: trazabilidadData, isLoading } = useFetchTrazabilidad(
    clienteId,
    contratoId,
    !!clienteId && !!contratoId
  );

  // useFetchTrazabilidad puede devolver { data: ... } o un array directo;
  // forzamos unknown primero para saltar el type guard estricto de TS.
  const trazabilidad = (trazabilidadData as unknown as ITrazabilidadCliente[]) ?? [];

  if (isLoading) {
    return (
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  if (!trazabilidad.length) {
    return (
      <Grid item xs={12}>
        <Box sx={{ py: 4 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No hay registros de trazabilidad disponibles.
          </Typography>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Timeline position="alternate">
        {trazabilidad.map((entry, index) => (
          <TimelineItem key={entry.id_trazabilidad}>
            <TimelineOppositeContent color="text.secondary">
              <Typography variant="caption">
                {dayjs(entry.fecha).format('DD/MM/YYYY HH:mm')}
              </Typography>
              <Typography variant="body2">{entry.usuario_nombre}</Typography>
            </TimelineOppositeContent>

            <TimelineSeparator>
              <TimelineDot color={getColorByTipoCambio(entry.tipo_cambio)} />
              {index < trazabilidad.length - 1 && <TimelineConnector />}
            </TimelineSeparator>

            <TimelineContent>
              <Typography variant="subtitle2" fontWeight="bold">
                {entry.campo_modificado}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {entry.tipo_cambio}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="error.main" component="span">
                  {entry.valor_anterior || '-'}
                </Typography>
                <Typography variant="caption" component="span" sx={{ mx: 0.5 }}>
                  &rarr;
                </Typography>
                <Typography variant="caption" color="success.main" component="span">
                  {entry.valor_nuevo || '-'}
                </Typography>
              </Box>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Grid>
  );
}
